import React, { useState } from 'react';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../firebase-config';
import { FiLock, FiUser, FiMail, FiPhone, FiBriefcase, FiMapPin, FiCheck, FiEye, FiEyeOff } from 'react-icons/fi';

export default function Settings({ merchantData }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!currentPassword) {
      setMessage({ type: 'error', text: 'Please enter your current password' });
      return;
    }
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setMessage({ type: 'success', text: 'Password updated successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setMessage({ type: 'error', text: 'Current password is incorrect' });
      } else if (err.code === 'auth/weak-password') {
        setMessage({ type: 'error', text: 'Password is too weak. Use at least 8 characters.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to change password. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const profileFields = [
    { icon: <FiBriefcase />, label: 'Business Name', value: merchantData?.businessName },
    { icon: <FiUser />, label: 'Contact Name', value: merchantData?.fullName },
    { icon: <FiMail />, label: 'Email', value: merchantData?.email || auth.currentUser?.email },
    { icon: <FiPhone />, label: 'Phone', value: merchantData?.phone },
    { icon: <FiMapPin />, label: 'Business Address', value: merchantData?.businessAddress },
    { icon: <FiBriefcase />, label: 'Merchant ID', value: merchantData?.merchantId },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-text-main">Account Settings</h1>

      {/* Profile Info */}
      <div className="bg-surface rounded-xl shadow-card p-5 sm:p-6">
        <h2 className="text-sm font-semibold text-text-main mb-4 flex items-center gap-2">
          <FiUser className="text-primary" size={16} />
          Profile Information
        </h2>
        <div className="space-y-3">
          {profileFields.map((field) => (
            <div key={field.label} className="flex items-start gap-3 p-3 bg-background rounded-lg">
              <span className="text-text-secondary mt-0.5">{field.icon}</span>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-text-secondary">{field.label}</p>
                <p className="text-sm font-medium text-text-main truncate">{field.value || '—'}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-text-secondary mt-4">
          Contact your admin to update profile information.
        </p>
      </div>

      {/* Change Password */}
      <div className="bg-surface rounded-xl shadow-card p-5 sm:p-6">
        <h2 className="text-sm font-semibold text-text-main mb-4 flex items-center gap-2">
          <FiLock className="text-primary" size={16} />
          Change Password
        </h2>

        {message && (
          <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-status-green-bg text-status-green'
              : 'bg-status-red-bg text-status-red'
          }`}>
            {message.type === 'success' && <FiCheck size={16} />}
            {message.text}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Current Password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 pr-10 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                placeholder="Enter current password"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-main"
              >
                {showCurrent ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 pr-10 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                placeholder="At least 8 characters"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-main"
              >
                {showNew ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-white rounded-lg font-semibold text-sm hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
